import React, { useEffect, useState } from "react";
import { get, invoke } from "lodash";
import htmlParser from "html-react-parser";
import ReactModal from "react-modal";
import CloseButton from "../../../component/CloseButton";
import {
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@material-ui/core";
import styles from "./PartnerComparisonTool.module.scss";
import Image from "../../../component/Image";
import { getModifiedValue, numberWithCommas } from "./helper";
import { CASHFLOW, DEFAULT_PARTNER } from "./constants";
import { useMediaQuery } from "../../../utils/Helper";
import { REMOVE_PARENTHESIS_REGEX } from "../helper";

const PartnerComparisonTool = (props) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const [partners, setPartners] = useState(get(props, "availablePartners", []));
  const [pinnedPartner, setPinnedPartner] = useState(
    get(props, "availablePartners[0]"),
  );

  const works = get(props, "currentData.works");
  const term = get(props, "currentData.term");
  const flow_through = get(props, "currentData.flow_through");
  const future = get(props, "currentData.future");
  
  // Build path array for lodash get - convert null to "null" string for object key access
  const currentDataPathArray = [works, term, flow_through, future]
    .filter((val) => val !== undefined)
    .map((val) => (val === null ? "null" : val));
  
  // Helper function to get partner data using array path
  const getPartnerData = (partner) => {
    if (!partner) return null;
    return get(
      props,
      ["advanceData", partner, ...currentDataPathArray],
      null
    );
  };

  const handleSort = (arr, pinName) => {
    const sorted = arr
      .filter((p) => p !== pinName)
      .sort((a, b) => {
        const advanceA = getModifiedValue(
          getPartnerData(a) || {},
          get(props, "cashflow_option_selected", CASHFLOW.STANDARD),
        ).total_advance;
        const advanceB = getModifiedValue(
          getPartnerData(b) || {},
          get(props, "cashflow_option_selected", CASHFLOW.STANDARD),
        ).total_advance;
        return advanceB - advanceA;
      });
    return [pinName, ...sorted];
  };

  useEffect(() => {
    const sortedPartners = handleSort(
      get(props, "availablePartners", []),
      pinnedPartner,
    );
    setPartners(sortedPartners);
  }, [pinnedPartner, get(props, "availablePartners")]);
  const rowHeader = [
    {
      title: (
        <div className={styles.columnHeader}>
          <span>{get(props, `currentData.term`)} year term</span>
          <span>Total Advance</span>
        </div>
      ),
      render: (p, partnerName) => (
        <div className={styles.firstRow}>
          <div className={styles.logoContainer}>
            {(get(p, "partner") || partnerName) !== DEFAULT_PARTNER && (
              <Image
                src={
                  get(p, "logo") ||
                  get(props, `partnersOptions.${partnerName}.logo`)
                }
              />
            )}
            <span>
              {(get(p, "partner") || partnerName) === DEFAULT_PARTNER
                ? "Current Distributor"
                : REMOVE_PARENTHESIS_REGEX.test(get(p, "partner") || partnerName) ? REMOVE_PARENTHESIS_REGEX.exec(get(p, "partner") || partnerName)[0] : get(p, "partner") || partnerName}
            </span>
          </div>
          <span className={styles.total}>
            {(() => {
              if (!p) return "N/A";
              const modifiedValue = getModifiedValue(
                p,
                get(props, "cashflow_option_selected", CASHFLOW.STANDARD),
              );
              return modifiedValue.total_advance
                ? `$${numberWithCommas(modifiedValue.total_advance)}`
                : "N/A";
            })()}
          </span>
          <button
            className={styles.offerBtn}
            disabled={!p || get(p, "partner") === pinnedPartner}
            onClick={() => setPinnedPartner(get(p, "partner"))}
          >
            {get(p, "partner") === pinnedPartner ? "Pinned" : "Pin"}
          </button>
        </div>
      ),
    },
    {
      title: "Upfront Advance",
      render: (p) =>
        !p
          ? "N/A"
          : `$${numberWithCommas(
              getModifiedValue(
                p,
                get(props, "cashflow_option_selected", CASHFLOW.STANDARD),
              ).catalog_advance,
            )}`,
    },
    {
      title: "New Release Advance",
      render: (p) =>
        getModifiedValue(
          p,
          get(props, "cashflow_option_selected", CASHFLOW.STANDARD),
        ).nr_advance
          ? `$${numberWithCommas(
              getModifiedValue(
                p,
                get(props, "cashflow_option_selected", CASHFLOW.STANDARD),
              ).nr_advance,
            )}`
          : "N/A",
    },
    {
      title: "Option Advance",
      render: (p) =>
        getModifiedValue(
          p,
          get(props, "cashflow_option_selected", CASHFLOW.STANDARD),
        ).option_advance
          ? `$${numberWithCommas(
              getModifiedValue(
                p,
                get(props, "cashflow_option_selected", CASHFLOW.STANDARD),
              ).option_advance,
            )}`
          : "N/A",
    },
    {
      title: "Recoupment Rate",
      render: (p) =>
        getModifiedValue(
          p,
          get(props, "cashflow_option_selected", CASHFLOW.STANDARD),
        ).royalty
          ? `${
              getModifiedValue(
                p,
                get(props, "cashflow_option_selected", CASHFLOW.STANDARD),
              ).royalty
            }%`
          : "N/A",
    },
    {
      title: "Distribution Fee",
      render: (p) =>
        get(p, "partner") === DEFAULT_PARTNER ? "Current Fee" : get(p, "fees"),
    },
    {
      title: "Features",
      render: (p) =>
        get(p, "featuresHTML") ? (
          <button
            className={styles.linkBtn}
            onClick={() => {
              invoke(props, "toggleFeatureModal", get(p, "partner"));
            }}
          >
            View Features
          </button>
        ) : (
          "N/A"
        ),
    },
    {
      title: "",
      render: (p) => (
        <button
          className={styles.offerBtn}
          disabled={!p}
          onClick={() => {
            invoke(props, "setSelectedPartner", { value: get(p, "partner") });
            invoke(props, "togglePartnerCompareTool");
          }}
        >
          View Offers
        </button>
      ),
    },
  ];

  return (
    <div className={styles.subContainer}>
      <div className={styles.titleContainer}>
        <h2>Compare beatBread Offers</h2>
        <p>
          Compare the features of the partner plans that are available below:
        </p>
      </div>
      <div className={styles.comparisonContent}>
        <TableContainer className={styles.tableContainerDesktop}>
          <Table
            style={{
              width: isMobile
                ? `${
                    (100 / 3) *
                    (get(props, "availablePartners.length", 0) + 1)
                  }%`
                : "100%",
            }}
          >
            <TableBody>
              {rowHeader.map((el, i) => (
                <TableRow key={`table-row-${i}`}>
                  <TableCell>{el.title}</TableCell>
                  {partners.map((partner) => (
                    <TableCell
                      className={styles.header}
                      key={`row-${i}-${partner}`}
                    >
                      {rowHeader[i].render(
                        getPartnerData(partner),
                        partner,
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <div className={styles.btnContainer}>
        <button onClick={get(props, "togglePartnerCompareTool")}>
          Go Back
        </button>
      </div>
    </div>
  );
};

export default PartnerComparisonTool;

export const FeatureDataModal = (props) => (
  <ReactModal
    isOpen={get(props, "isOpen")}
    shouldCloseOnOverlayClick
    shouldCloseOnEsc
    onRequestClose={get(props, "toggleFeatureModal")}
    className={styles.featureDataModal}
    overlayClassName={styles.modalOverlay}
  >
      <CloseButton
        onClick={get(props, "toggleFeatureModal")}
        className={styles.closeBtn}
        data-testid="span-close"
      />
    <div className={styles.modalContent}>
      {htmlParser(get(props, "featureData.html", ""))}
    </div>
  </ReactModal>
);
